import { ReactNode } from 'react';
import { Track } from '../../../components/ui';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "2026-10-05" or a full ISO timestamp -> "5 Oct" (design date labels). Unparseable input is returned as is. */
export function dayMonth(iso: string | null | undefined): string {
  if (!iso) return '';
  // A bare date (week_start) is read as written; a timestamp is shown in the viewer's own day.
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (m) return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `${d.getDate()} ${MONTHS[d.getMonth()]}`;
}

/** "+4.1" / "−3.1" with a typographic minus (design `sgn`); zero reads "±0.0". */
export function signed1(v: number): string {
  const r = Math.round(v * 10) / 10;
  return `${r > 0 ? '+' : r < 0 ? '−' : '±'}${Math.abs(r).toFixed(1)}`;
}

/** Chip tone for a delta shown with `signed1`: a value that rounds to ±0.0 is neutral. */
export function deltaTone(v: number): 'up' | 'down' | 'neutral' {
  const r = Math.round(v * 10) / 10;
  return r === 0 ? 'neutral' : r > 0 ? 'up' : 'down';
}

/** Design `.key` swatch + label for chart legends. */
export function Key({ tone, children }: { tone: 'accent' | 'slate' | 'navy' | 'faint'; children: ReactNode }) {
  const bg = { accent: 'bg-accent', navy: 'bg-navy dark:bg-mist-fixed', faint: 'bg-on-navy-muted', slate: 'bg-slate' }[tone];
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className={`inline-block h-2.5 w-2.5 rounded-[3px] ${bg}`} />{children}
    </span>
  );
}

export function Legend({ children }: { children: ReactNode }) {
  return <span className="flex flex-wrap gap-3.5 text-xs text-muted">{children}</span>;
}

/** Design `.subrow`: a flexible track, then a 52px right-aligned value. `lo` paints the track accent. */
export function SubRow({ value, lo, label, bold = false }: { value: number | null; lo?: boolean; label: string; bold?: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      {value != null && <Track value={value} tone={lo ? 'accent' : 'slate'} label={label} className="min-w-[80px] flex-1" />}
      <span className={`w-[52px] text-right font-mono tabular-nums ${bold ? 'font-semibold' : 'font-medium'}`}>
        {value == null ? '—' : `${value.toFixed(1)}%`}
      </span>
    </div>
  );
}

/** "Grade 10" + "B" -> "10B" (design `secShort`); falls back to "Grade X B" for free-text grade names. */
export function shortSection(gradeName: string | undefined, sectionName: string | undefined): string {
  if (!gradeName || !sectionName) return '';
  const n = gradeName.match(/\d+/)?.[0];
  return n ? `${n}${sectionName}` : `${gradeName} ${sectionName}`;
}
